import {
  boundaryTerritoryDetailsZoomedLoadingError, boundaryTerritoryDetailsZoomedLoadingStart, boundaryTerritoryDetailsZoomedLoadingSuccess,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.actionCreators';
import { boundaryTerritoryGroupUpdateSuccess } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { copy } from '../../_shared/utils/collections/collections';
import { type BoundaryTerritoryGroupsAction } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import {
  BOUNDARY_TERRITORY_DELETE, BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS,
} from '../boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import { type BoundaryTerritoryDetailsAction } from './boundaryTerritoryDetails.action';
import {
  BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_ERROR,
  BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_REQUEST,
  BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_SUCCESS,
  BOUNDARY_TERRITORY_DETAILS_UPDATE_MANUAL_ASSIGNMENTS,
} from './boundaryTerritoryDetails.actionTypes';
import {
  mergeBoundaryTerritoryGroupDetailsItems,
  removeTerritoryAssignmentsFromBoundaryTerritoryDetails,
} from './boundaryTerritoryDetails.helpers';
import { type BoundaryTerritoryDetailsState } from './boundaryTerritoryDetails.types';

const boundaryTerritoryGroupsInitialState: BoundaryTerritoryDetailsState = {
  territoryDetails: {
    data: new Map(),
    filterHash: '',
  },
  isError: false,
  isLoadingFullDetails: false,
  isLoadingBoundsDetails: false,
};

export const boundaryTerritoryDetailsReducer = (
  state: BoundaryTerritoryDetailsState = boundaryTerritoryGroupsInitialState,
  action: BoundaryTerritoryDetailsAction | BoundaryTerritoryGroupsAction
): BoundaryTerritoryDetailsState => {

  if (boundaryTerritoryGroupUpdateSuccess.match(action)) {
    const { boundaryTerritoryGroups, fetchBoundaryDetails } = action.payload;
    const updatedData = new Map(state.territoryDetails.data);
    for (const boundaryTerritoryGroup of boundaryTerritoryGroups) {
      if (fetchBoundaryDetails) {
        updatedData.delete(boundaryTerritoryGroup.boundaryTerritoryGroupId);
      }
    }

    return {
      ...state,
      territoryDetails: {
        ...state.territoryDetails,
        data: updatedData,
      },
    };
  }

  if (boundaryTerritoryDetailsZoomedLoadingStart.match(action)) {
    return {
      ...state,
      isError: false,
      isLoadingBoundsDetails: true,
    };
  }

  if (boundaryTerritoryDetailsZoomedLoadingError.match(action)) {
    return {
      ...state,
      isError: true,
      isLoadingBoundsDetails: false,
    };
  }

  if (boundaryTerritoryDetailsZoomedLoadingSuccess.match(action)) {
    const isSameFilter = action.payload.filterHash === state.territoryDetails.filterHash;
    const newDetails = isSameFilter ? new Map(state.territoryDetails.data) : new Map();

    for (const result of action.payload.results) {
      const existingDetails = newDetails.get(result.boundaryTerritoryGroupId);
      const mergedDetails = existingDetails
        ? mergeBoundaryTerritoryGroupDetailsItems(existingDetails, result.details.boundaryDetails)
        : result.details.boundaryDetails;

      newDetails.set(
        result.boundaryTerritoryGroupId,
        mergedDetails,
      );
    }

    return {
      ...state,
      territoryDetails: {
        data: newDetails,
        filterHash: action.payload.filterHash,
      },
      isLoadingBoundsDetails: false,
    };
  }

  switch (action.type) {
    case BOUNDARY_TERRITORY_DELETE: {
      return removeTerritoryAssignmentsFromBoundaryTerritoryDetails(
        state, action.payload.boundaryTerritoryGroupId, action.payload.territoryId
      );
    }

    case BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_REQUEST: {
      return {
        ...state,
        isError: false,
        isLoadingFullDetails: true,
      };
    }

    case BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_ERROR: {
      return {
        ...state,
        isError: true,
        isLoadingFullDetails: false,
      };
    }

    case BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS: {
      return {
        ...state,
        territoryDetails: boundaryTerritoryGroupsInitialState.territoryDetails,
      };
    }

    case BOUNDARY_TERRITORY_DETAILS_FETCH_DETAILS_SUCCESS: {
      const isSameFilter = action.payload.filterHash === state.territoryDetails.filterHash;
      const newDetails = isSameFilter ? new Map(state.territoryDetails.data) : new Map();

      for (const result of action.payload.results) {
        newDetails.set(
          result.boundaryTerritoryGroupId,
          result.details.boundaryDetails,
        );
      }

      return {
        ...state,
        territoryDetails: {
          data: newDetails,
          filterHash: action.payload.filterHash,
        },
        isLoadingFullDetails: false,
      };
    }

    case BOUNDARY_TERRITORY_DETAILS_UPDATE_MANUAL_ASSIGNMENTS: {
      const currentTerritoryDetails = state.territoryDetails.data.get(action.payload.boundaryTerritoryGroupId);
      if (!currentTerritoryDetails) {
        return state;
      }

      const newDetails = {
        ...currentTerritoryDetails,
        manualAssignments: action.payload.manualAssignments,
      };

      return {
        ...state,
        territoryDetails: {
          data: copy.andReplace(state.territoryDetails.data, action.payload.boundaryTerritoryGroupId, newDetails),
          filterHash: state.territoryDetails.filterHash,
        },
      };
    }

    default:
      return state;
  }
};
